import React, { useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WeekSchedule } from "@/components/WeekSchedule";
import { WeekAgenda } from "@/components/WeekAgenda";
import { fmtMinutes, DuePicker } from "@/components/TaskShared";
import { capacityByDay, capacityDraw, loadTone } from "@/lib/capacity";
import { todayStr } from "@/lib/tz";
import { toast } from "sonner";
import { CalendarDays, List, ListChecks } from "lucide-react";

const VIEWS = [["calendar", "Calendar", CalendarDays], ["week", "Week list", List], ["all", "All tasks", ListChecks]];
const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (iso, n) => { const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + n); return isoLocal(d); };
const mondayOf = (iso) => { const d = new Date(iso + "T00:00:00"); return addDays(iso, -((d.getDay() + 6) % 7)); };
const TONE_TEXT = { ok: "text-[#1f9d55]", near: "text-[#b7791f]", over: "text-[#c0392b]", off: "text-[#9FA1A4]" };

function RangeSummary({ label, start, cap, testid }) {
  let used = 0, total = 0, busiest = null;
  for (let i = 0; i < 7; i++) {
    const d = addDays(start, i); const c = cap.get(d);
    used += c.used; total += c.cap;
    if (c.cap > 0 && c.used > c.cap && (!busiest || c.used - c.cap > busiest.over)) busiest = { d, over: c.used - c.cap };
  }
  const tone = busiest ? "over" : loadTone(used, total);
  return (
    <div data-testid={testid} className="rounded-2xl border border-[#EEEFEF] bg-white px-4 py-3 min-w-[200px]">
      <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">{label}</p>
      <p className={`font-display text-lg font-semibold ${TONE_TEXT[tone]}`}>{fmtMinutes(used) || "0m"} <span className="text-sm font-normal text-[#9FA1A4]">of {fmtMinutes(total) || "0m"}{total > 0 ? ` · ${Math.round((used / total) * 100)}%` : ""}</span></p>
      {busiest && <p className="text-[11px] text-[#c0392b]">Over on {new Date(busiest.d + "T00:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "numeric" })} by {fmtMinutes(busiest.over)}</p>}
    </div>
  );
}

function AllTasks({ tasks, onOpen, onDate }) {
  const today = todayStr();
  const open = tasks.filter((t) => !t.is_completed).sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"));
  if (!open.length) return <p className="rounded-2xl border border-dashed border-[#e2e2ea] p-10 text-center text-sm text-[#9FA1A4]">No open tasks.</p>;
  return (
    <div className="rounded-2xl border border-[#EEEFEF] bg-white divide-y divide-[#f4f4f7]" data-testid="manage-all-tasks">
      {open.map((t) => (
        <div key={t.id} data-testid={`manage-task-${t.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#FAFAFE]">
          <button onClick={() => onOpen(t.id)} className="flex-1 min-w-0 text-left">
            <p className="text-sm text-[#06060C] truncate"><span className="font-mono-data text-[11px] text-[#5b2fd6] mr-2">{t.code}</span>{t.title}</p>
            <p className="text-[11px] text-[#9FA1A4] truncate">{[t.project?.name || t.project_name, t.client?.name].filter(Boolean).join(" · ")}{t.is_internal ? " · Internal" : ""}</p>
          </button>
          <span className="font-mono-data text-[11px] text-[#9FA1A4] w-16 text-right">{capacityDraw(t) ? fmtMinutes(capacityDraw(t)) : "—"}</span>
          <div className={`w-36 rounded-md ${t.due_date && t.due_date < today ? "ring-1 ring-[#e5484d]/40 bg-[#FDECEC]" : ""}`}><DuePicker compact value={t.due_date || null} onChange={(d) => d !== t.due_date && onDate(t, d)} testid={`manage-task-date-${t.id}`} /></div>
        </div>
      ))}
    </div>
  );
}

export function ManageSchedule({ data, users, onOpen, onChanged }) {
  const [uid, setUid] = useState("");
  const [view, setView] = useState("calendar");
  const person = users.find((u) => u.id === uid);
  const mine = useMemo(() => (data.tasks || []).filter((t) => t.assignee_id === uid && !t.archived), [data.tasks, uid]);
  const cap = useMemo(() => {
    const c = capacityByDay(mine, person);
    const draw = Object.fromEntries(mine.map((t) => [t.id, capacityDraw(t)]));
    return { ...c, drawOf: (id) => draw[id] || 0 };
  }, [mine, person]);
  const put = async (id, body, msg) => { try { await api.put(`/console/tasks/${id}`, body); toast.success(msg); onChanged(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const thisWeek = mondayOf(todayStr());
  return (
    <div data-testid="workload-manage">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Select value={uid || "none"} onValueChange={(v) => setUid(v === "none" ? "" : v)}>
          <SelectTrigger data-testid="manage-user" className="w-64 h-9 bg-white border-[#EEEFEF] text-sm"><SelectValue placeholder="Choose a teammate…" /></SelectTrigger>
          <SelectContent className="bg-white border-[#EEEFEF] max-h-72"><SelectItem value="none">Choose a teammate…</SelectItem>{users.filter((u) => !u.is_agent || u.visibility === "on").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.is_agent ? " (AI)" : ""}</SelectItem>)}</SelectContent>
        </Select>
        {uid && (
          <div className="inline-flex items-center gap-1 rounded-full border border-[#EEEFEF] bg-white p-1 ml-auto">
            {VIEWS.map(([k, label, Icon]) => <button key={k} data-testid={`manage-view-${k}`} onClick={() => setView(k)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${view === k ? "bg-[#06060C] text-white" : "text-[#595D62] hover:bg-[#F6F6FD]"}`}><Icon className="h-3.5 w-3.5" /> {label}</button>)}
          </div>
        )}
      </div>
      {!uid ? (
        <div className="rounded-2xl border border-dashed border-[#e2e2ea] bg-[#FCFCFE] p-12 text-center text-sm text-[#9FA1A4]" data-testid="manage-empty">Pick a teammate to see every task they have, across all projects, and manage their schedule.</div>
      ) : (
        <>
          <div className="flex gap-3 mb-4 flex-wrap">
            <RangeSummary label="This week" start={thisWeek} cap={cap} testid="manage-summary-this" />
            <RangeSummary label="Next week" start={addDays(thisWeek, 7)} cap={cap} testid="manage-summary-next" />
            <div className="rounded-2xl border border-[#EEEFEF] bg-white px-4 py-3">
              <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">Open tasks</p>
              <p className="font-display text-lg font-semibold text-[#06060C]" data-testid="manage-open-count">{mine.filter((t) => !t.is_completed).length} <span className="text-sm font-normal text-[#9FA1A4]">· {mine.filter((t) => !t.is_completed && !t.due_date).length} undated</span></p>
            </div>
          </div>
          {view === "calendar" && <WeekSchedule tasks={mine} me={person} canEdit onOpenTask={onOpen} onReschedule={(id, body) => put(id, body, "Task rescheduled")} capacity={cap} />}
          {view === "week" && <WeekAgenda tasks={mine} me={person} canEdit onOpenTask={onOpen} onReschedule={(id, body) => put(id, body, "Task rescheduled")} capacity={cap} />}
          {view === "all" && <AllTasks tasks={mine} onOpen={onOpen} onDate={(t, d) => put(t.id, { due_date: d }, d ? "Due date updated" : "Due date cleared")} />}
        </>
      )}
    </div>
  );
}
